import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  UploadSimple, DownloadSimple, Trash, UsersThree, FolderSimple, FolderOpen, Sparkle,
  Image as ImageIcon, FilmStrip, MusicNotes, FilePdf, FileText, File as FileIcon, CircleNotch,
  LinkSimple, Copy, XCircle, Clock,
} from "@phosphor-icons/react";

const KIND_ICON = { image: ImageIcon, video: FilmStrip, audio: MusicNotes, pdf: FilePdf, text: FileText, doc: FileText, other: FileIcon };
const KIND_COLOR = { image: "#00c9a7", video: "#a970ff", audio: "#f59e0b", pdf: "#ef4444", text: "#4dabf5", doc: "#4dabf5", other: "#8892a6" };
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} МБ` : `${Math.max(1, Math.round(b / 1024))} КБ`);
const dlUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/assistant/files/${id}/download`;
const shareUrl = (token) => `${window.location.origin}/s/${token}`;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
    return true;
  }
};

const EXPIRES = [["1d", "1 день"], ["7d", "7 дней"], ["30d", "30 дней"], ["never", "Бессрочно"]];

function ShareLinkPopover({ f, onChanged }) {
  const [expires, setExpires] = useState("never");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/assistant/files/${f.id}/share-link`, { expires });
      await copyText(shareUrl(r.token));
      toast.success("Ссылка создана и скопирована в буфер обмена");
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/assistant/files/${f.id}/share-link`);
      toast.success("Ссылка отозвана — файл больше не доступен по ней");
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const expired = f.share?.expires_at && new Date(f.share.expires_at) < new Date();

  return (
    <PopoverContent className="glass-modal w-72 p-3" align="end" data-testid={`share-link-popover-${f.id}`}>
      {f.share ? (
        <div className="space-y-2.5">
          <div className="flex items-center gap-1.5 text-sm font-semibold">
            <LinkSimple size={15} weight="bold" className="text-[#00c9a7]" /> Публичная ссылка активна
          </div>
          <div className="flex items-center gap-1.5">
            <Input readOnly value={shareUrl(f.share.token)} className="h-8 text-[11px] num" data-testid={`share-link-url-${f.id}`}
              onFocus={(e) => e.target.select()} />
            <Button size="sm" variant="secondary" className="h-8 px-2 shrink-0" data-testid={`share-link-copy-${f.id}`}
              onClick={async () => { await copyText(shareUrl(f.share.token)); toast.success("Скопировано"); }}>
              <Copy size={14} />
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1">
            <Clock size={11} />
            {expired ? "Срок действия истёк — отзовите и создайте новую" :
              f.share.expires_at
                ? `Действует до ${new Date(f.share.expires_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "numeric" })}`
                : "Бессрочная — пока вы её не отзовёте"}
          </p>
          <div className="flex flex-col items-center gap-1.5 py-1" data-testid={`share-link-qr-${f.id}`}>
            <div className="bg-white p-2.5 rounded-xl">
              <QRCodeSVG value={shareUrl(f.share.token)} size={124} level="M" />
            </div>
            <p className="text-[10px] text-muted-foreground">Сканируйте с телефона, чтобы открыть файл</p>
          </div>
          <Button size="sm" variant="destructive" className="w-full h-8" onClick={revoke} disabled={busy} data-testid={`share-link-revoke-${f.id}`}>
            {busy ? <CircleNotch size={14} className="animate-spin" /> : <><XCircle size={14} className="mr-1.5" /> Отозвать ссылку</>}
          </Button>
        </div>
      ) : (
        <div className="space-y-2.5">
          <div className="flex items-center gap-1.5 text-sm font-semibold">
            <LinkSimple size={15} weight="bold" className="text-primary" /> Поделиться по ссылке
          </div>
          <p className="text-[11px] text-muted-foreground">Любой человек с ссылкой сможет открыть и скачать файл — без входа в приложение.</p>
          <div className="flex gap-1 flex-wrap">
            {EXPIRES.map(([id, label]) => (
              <button key={id} onClick={() => setExpires(id)} data-testid={`share-expire-${id}-${f.id}`}
                className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${expires === id ? "bg-primary text-white font-semibold" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                {label}
              </button>
            ))}
          </div>
          <Button size="sm" className="w-full h-8" onClick={create} disabled={busy} data-testid={`share-link-create-${f.id}`}>
            {busy ? <CircleNotch size={14} className="animate-spin" /> : <><LinkSimple size={14} className="mr-1.5" /> Создать и скопировать</>}
          </Button>
        </div>
      )}
    </PopoverContent>
  );
}

export default function FilesTab() {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [organizing, setOrganizing] = useState(false);
  const [folder, setFolder] = useState("all");
  const [newFolder, setNewFolder] = useState("");
  const inputRef = useRef(null);

  const load = () => api.get("/assistant/files").then((r) => setData(r.data)).catch(() => setData({ files: [], folders: [] }));
  useEffect(() => { load(); }, []);

  const processing = (data?.files || []).some((f) => f.status === "processing");
  useEffect(() => {
    if (!processing) return;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [processing]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/assistant/files/upload", fd);
      } catch (err) { toast.error(`${f.name}: ${formatApiError(err)}`); }
    }
    setUploading(false);
    toast.success(files.length > 1 ? `Загружено файлов: ${files.length}` : "Файл загружен — AI анализирует");
    load();
  };

  const organize = async () => {
    setOrganizing(true);
    try {
      const { data: r } = await api.post("/assistant/files/organize", { instruction });
      toast.success(r.message);
      setInstruction("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setOrganizing(false);
  };

  const move = async (f, target) => {
    try {
      await api.patch(`/assistant/files/${f.id}`, { folder: target });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const share = async (f) => {
    try {
      const { data: r } = await api.post(`/assistant/files/${f.id}/share-to-family`);
      toast.success(r.message);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (f) => {
    await api.delete(`/assistant/files/${f.id}`);
    toast.success("Файл удалён");
    load();
  };

  if (!data) return <div className="glass-card h-48 animate-pulse" />;

  const files = data.files.filter((f) => folder === "all" || f.folder === folder);
  const counts = data.files.reduce((acc, f) => { const k = f.folder || ""; acc[k] = (acc[k] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="files-tab">
      <div className="glass-card p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <input ref={inputRef} type="file" multiple className="hidden" onChange={upload} data-testid="file-upload-input" />
        <Button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="file-upload-button" className="shrink-0">
          {uploading ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <UploadSimple size={16} className="mr-1.5" />}
          {uploading ? "Загружаем…" : "Загрузить файлы"}
        </Button>
        <p className="text-xs text-muted-foreground">
          Фото, видео, PDF, документы, аудио — до 50 МБ. Фото и PDF читает AI, аудио расшифровывается. Прикрепляйте файлы в «Диалоге» скрепкой и спрашивайте о содержимом.
        </p>
      </div>

      <div className="glass-card p-4">
        <div className="flex items-center gap-1.5 text-sm font-semibold mb-2"><Sparkle size={15} weight="fill" className="text-[#0084ff]" /> AI-организация</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={instruction} onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !organizing && organize()}
            placeholder="Например: чеки в папку «Чеки», анализы — в «Медицина»" data-testid="organize-input" />
          <Button onClick={organize} disabled={organizing || !data.files.length} data-testid="organize-button" variant="secondary" className="shrink-0">
            {organizing ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <FolderOpen size={16} className="mr-1.5" />}
            {organizing ? "Раскладываем…" : "Сгруппировать"}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">Пустая инструкция = «разложи всё по смыслу». Работает и голосом/текстом в «Диалоге»: «сгруппируй мои файлы».</p>
      </div>

      {data.files.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setFolder("all")} data-testid="folder-chip-all"
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${folder === "all" ? "bg-primary text-white font-semibold" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            Все · {data.files.length}
          </button>
          {data.folders.map((fo) => (
            <button key={fo} onClick={() => setFolder(fo)} data-testid={`folder-chip-${fo}`}
              className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full transition-colors ${folder === fo ? "bg-primary text-white font-semibold" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              <FolderSimple size={12} weight="fill" /> {fo} · {counts[fo] || 0}
            </button>
          ))}
        </div>
      )}

      {files.length === 0 ? (
        <div className="glass-card p-10 text-center text-muted-foreground text-sm" data-testid="files-empty">
          {data.files.length === 0 ? "Пока нет файлов — загрузите первый!" : "В этой папке пусто"}
        </div>
      ) : (
        <div className="glass-card divide-y divide-border/40">
          {files.map((f) => {
            const Icon = KIND_ICON[f.kind] || FileIcon;
            return (
              <div key={f.id} className="flex items-start gap-3 px-4 py-3" data-testid={`file-row-${f.id}`}>
                {f.kind === "image" ? (
                  <img src={dlUrl(f.id)} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0 border border-border/60" loading="lazy" />
                ) : (
                  <div className="w-11 h-11 rounded-lg bg-secondary/60 flex items-center justify-center shrink-0">
                    <Icon size={22} weight="duotone" style={{ color: KIND_COLOR[f.kind] }} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium truncate max-w-[220px]">{f.name}</span>
                    {f.folder && <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary"><FolderSimple size={10} weight="fill" />{f.folder}</span>}
                    {f.status === "processing" && (
                      <span className="flex items-center gap-1 text-[10px] text-[#0084ff] animate-pulse" data-testid={`file-processing-${f.id}`}>
                        <CircleNotch size={10} className="animate-spin" /> AI анализирует…
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground num">
                    {fmtSize(f.size)} · {new Date(f.created_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}
                  </div>
                  {f.summary && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{f.summary}</p>}
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <a href={dlUrl(f.id)} target="_blank" rel="noreferrer" download={f.name} title="Скачать"
                    data-testid={`file-download-${f.id}`} className="p-1.5 text-muted-foreground hover:text-primary transition-colors">
                    <DownloadSimple size={16} />
                  </a>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button title="Переместить в папку" data-testid={`file-move-${f.id}`}
                        className="p-1.5 text-muted-foreground hover:text-primary transition-colors"><FolderSimple size={16} /></button>
                    </PopoverTrigger>
                    <PopoverContent className="glass-modal w-56 p-2 space-y-1" align="end">
                      {f.folder && (
                        <button onClick={() => move(f, "")} className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-secondary transition-colors text-muted-foreground">
                          Убрать из «{f.folder}»
                        </button>
                      )}
                      {data.folders.filter((fo) => fo !== f.folder).map((fo) => (
                        <button key={fo} onClick={() => move(f, fo)}
                          className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-secondary transition-colors flex items-center gap-1.5">
                          <FolderSimple size={12} /> {fo}
                        </button>
                      ))}
                      <div className="flex gap-1 pt-1">
                        <Input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder="Новая папка"
                          className="h-8 text-xs" data-testid={`file-newfolder-input-${f.id}`} />
                        <Button size="sm" className="h-8 px-2" disabled={!newFolder.trim()}
                          onClick={() => { move(f, newFolder.trim()); setNewFolder(""); }} data-testid={`file-newfolder-ok-${f.id}`}>OK</Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button title="Поделиться по ссылке" data-testid={`file-sharelink-${f.id}`}
                        className={`p-1.5 transition-colors ${f.share ? "text-[#00c9a7]" : "text-muted-foreground hover:text-primary"}`}>
                        <LinkSimple size={16} weight={f.share ? "bold" : "regular"} />
                      </button>
                    </PopoverTrigger>
                    <ShareLinkPopover f={f} onChanged={load} />
                  </Popover>
                  <button onClick={() => share(f)} title="Отправить семье" data-testid={`file-share-${f.id}`}
                    className="p-1.5 text-muted-foreground hover:text-[#00c9a7] transition-colors"><UsersThree size={16} /></button>
                  <button onClick={() => remove(f)} title="Удалить" data-testid={`file-delete-${f.id}`}
                    className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><Trash size={16} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
